import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import PlaylistPlayIcon from "@mui/icons-material/PlaylistPlay";
import { tokens } from "../theme";
import { EditDrawer } from "./EditDrawer";
import type {
  SignageAssignment,
  SignageAssignmentPatch,
  SignagePlaylist,
  SignageScreenGroup,
} from "@shared/types/signage";
import { PermissionGate } from "../auth/access";

/** The third state, and the reason this dialog is not two dropdowns. */
const INHERIT = "__inherit__";

/**
 * What a group plays.
 *
 * ## Why every field offers "inherit"
 *
 * A group is a LAYER, and a layer's most important answer is often "I have no
 * opinion about this". A group made to put one channel on the treadmill screens
 * must not also decide their sound - fifty walls would silently take a setting
 * nobody chose, and the location's own answer would stop being reachable.
 *
 * So each control has three states, not two: inherit, and the two real values.
 * "Inherit" is the default for a group that has never been given an opinion,
 * and choosing it again RETRACTS one - which the wire expresses as `null`,
 * because a boolean cannot say "nothing to say" on its own.
 *
 * ## Why this is not the full assignment editor
 *
 * A screen's editor offers a weekly plan, campaigns, break rates and
 * orientation. Those are either physical facts about one wall (orientation) or
 * a week somebody builds screen by screen. What a GROUP is for is the two
 * settings you would otherwise change fifty times: what plays by default, and
 * whether it makes noise.
 */
export default function SignageGroupContentDialog({
  open,
  group,
  current,
  playlists,
  saving,
  error,
  onClose,
  onSave,
}: {
  open: boolean;
  /**
   * The group whose content is being set. Kept by the page while the drawer
   * slides out, so the title does not blank mid-animation.
   */
  group: SignageScreenGroup | null;
  /** What the group already says, or null if it has never said anything. */
  current: SignageAssignment | null;
  playlists: SignagePlaylist[];
  saving: boolean;
  error: string | null;
  onClose: () => void;
  /** Persist. Resolves true on success, which slides the drawer out. */
  onSave: (patch: SignageAssignmentPatch) => Promise<boolean>;
}) {
  const { t } = useTranslation(["signage", "common"]);
  const [playlistId, setPlaylistId] = useState(INHERIT);
  const [sound, setSound] = useState(INHERIT);

  useEffect(() => {
    if (!open || !group) return;
    setPlaylistId(current?.defaultPlaylistId || INHERIT);
    // `undefined` and `null` both mean the group never said - only a real
    // boolean is an opinion.
    setSound(
      current?.soundEnabled === true
        ? "on"
        : current?.soundEnabled === false
          ? "off"
          : INHERIT
    );
  }, [open, group, current]);

  const screens = group?.screenIds.length ?? 0;

  return (
    <EditDrawer
      open={open}
      onClose={onClose}
      title={t("signageGroupContentDialog.title", { name: group?.name })}
      subtitle={t("signageGroupContentDialog.subtitle", { count: screens })}
      icon={<PlaylistPlayIcon />}
      footerTop={
        error && (
          <Typography variant="bodySm" sx={{ pb: 1, color: tokens.error }}>
            {error}
          </Typography>
        )
      }
      actions={(dismiss) => (
        <>
          <Button onClick={() => dismiss()}>
            {t("common:buttons.cancel")}
          </Button>
          <Box sx={{ flex: 1 }} />
          <PermissionGate
            resource="signage"
            level="edit"
            deniedProps={{ disabled: true }}
          >
            <Button
              variant="contained"
              disabled={saving}
              onClick={() =>
                // The page saves; on success the drawer slides out first.
                void onSave({
                  // "" is how this wire retracts a playlist: the store writes null,
                  // and the resolver skips null exactly as it skips absent.
                  defaultPlaylistId: playlistId === INHERIT ? "" : playlistId,
                  // `null` is the retraction. `false` would be an instruction to be
                  // silent, which is a different and much louder thing.
                  soundEnabled:
                    sound === INHERIT ? null : sound === "on" ? true : false,
                }).then((ok) => ok && dismiss())
              }
            >
              {saving ? t("common:states.saving") : t("common:buttons.save")}
            </Button>
          </PermissionGate>
        </>
      )}
    >
      <Stack gap={2.5}>
        <TextField
          select
          fullWidth
          size="small"
          label={t("signageGroupContentDialog.defaultChannel")}
          value={playlistId}
          onChange={(e) => setPlaylistId(e.target.value)}
          inputProps={{
            "aria-label": t("signageGroupContentDialog.defaultChannel"),
          }}
          helperText={t("signageGroupContentDialog.defaultChannelHelp")}
        >
          <MenuItem value={INHERIT}>
            {t("signageGroupContentDialog.inherit")}
          </MenuItem>
          {playlists.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          fullWidth
          size="small"
          label={t("common:labels.sound")}
          value={sound}
          onChange={(e) => setSound(e.target.value)}
          inputProps={{ "aria-label": t("common:labels.sound") }}
          helperText={t("signageGroupContentDialog.soundHelp")}
        >
          <MenuItem value={INHERIT}>
            {t("signageGroupContentDialog.inherit")}
          </MenuItem>
          <MenuItem value="on">{t("common:labels.on")}</MenuItem>
          <MenuItem value="off">{t("common:labels.off")}</MenuItem>
        </TextField>

        {/*
            The rule an operator will otherwise discover by being surprised: a
            screen that was set directly keeps winning, and this dialog cannot
            override it. Said here rather than after the fact.
          */}
        <Alert severity="info" sx={{ typography: "bodySm" }}>
          {t("signageGroupContentDialog.precedence")}
        </Alert>
      </Stack>
    </EditDrawer>
  );
}
